import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircleIcon, XCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const Unsubscribe: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    data?: {
      email: string;
      unsubscribedAt: string;
      alreadyUnsubscribed: boolean;
    };
  } | null>(null);

  useEffect(() => {
    processUnsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [token]);

  const processUnsubscribe = async () => {
    try {
      setLoading(true);

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://studi.unasca.it/api';
      const response = await axios.get(`${API_BASE_URL}/unsubscribe/${token}`);

      setResult(response.data);
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || 'Errore durante la disiscrizione. Ti preghiamo di contattarci.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-lg text-gray-600 dark:text-gray-400">Elaborazione in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo UNASCA */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/logo-unasca.png"
              alt="UNASCA"
              className="h-16 mx-auto"
            />
          </Link>
        </div>

        {/* Card Risultato */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {result?.success ? (
            // Disiscrizione completata
            <>
              <div className="flex items-center justify-center mb-6">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-3">
                  <CheckCircleIcon className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
                {result.data?.alreadyUnsubscribed
                  ? 'Già disiscritto'
                  : 'Disiscrizione completata'}
              </h1>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <EnvelopeIcon className="h-5 w-5 flex-shrink-0" />
                  <span>{result.data?.email}</span>
                </div>
              </div>

              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                {result.data?.alreadyUnsubscribed
                  ? 'Questo indirizzo email risulta già disiscritto dalle nostre comunicazioni.'
                  : 'La tua disiscrizione è stata registrata con successo. Non riceverai più email da UNASCA.'}
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Nota:</strong> Se hai cambiato idea, contattaci a{' '}
                  <a
                    href="mailto:info@studi.unasca.it"
                    className="underline hover:text-blue-600"
                  >
                    info@studi.unasca.it
                  </a>
                  {' '}per riattivare le comunicazioni.
                </p>
              </div>
            </>
          ) : (
            // Errore
            <>
              <div className="flex items-center justify-center mb-6">
                <div className="bg-red-100 dark:bg-red-900/30 rounded-full p-3">
                  <XCircleIcon className="h-12 w-12 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
                Ops! Qualcosa è andato storto
              </h1>

              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                {result?.message}
              </p>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Hai bisogno di aiuto?</strong> Contattaci a{' '}
                  <a
                    href="mailto:info@studi.unasca.it"
                    className="underline hover:text-yellow-600"
                  >
                    info@studi.unasca.it
                  </a>
                  {' '}e il nostro team ti assisterà.
                </p>
              </div>
            </>
          )}

          {/* Pulsante Torna al sito */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Torna al sito
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} UNASCA - Unione Nazionale Autoscuole e Studi di Consulenza Automobilistica
          </p>
          <div className="mt-2 flex items-center justify-center gap-4 text-xs text-gray-400 dark:text-gray-500">
            <a href="https://studi.unasca.it" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              studi.unasca.it
            </a>
            <span>•</span>
            <a href="mailto:info@studi.unasca.it" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              info@studi.unasca.it
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
